import { MODULES, type ModuleDefinition, type ModuleKey } from "../modules/registry";
import { canAccessModule } from "./access";
import { PROPERTY_OPTIONS } from "./accessGrants";
import { resolveEffectiveAccess, type EffectiveAccessContext } from "./effectiveAccess";
import type { AuthenticatedUser } from "./types";

export type ResolvedPropertyContext = {
  id: string;
  label: string;
  isDefault: boolean;
};

export type ResolvedNavigationContext = {
  access: EffectiveAccessContext;
  properties: ResolvedPropertyContext[];
  accessibleModules: ModuleDefinition[];
  visibleModules: ModuleDefinition[];
  defaultLandingPath: string | null;
  state: "ready" | "no_role" | "no_property" | "no_modules";
};

function getModulePath(module: ModuleDefinition): string {
  if (module.path) return module.path;
  return `/module/${module.key}`;
}

function getPropertyLabel(tenantId: string) {
  const knownProperty = PROPERTY_OPTIONS.find((property) => property.id === tenantId);
  if (knownProperty) return knownProperty.label;
  return tenantId.toUpperCase() === "HQ" ? "Xentros HQ" : `Xentros ${tenantId}`;
}

function resolveAccessibleModules(user: AuthenticatedUser | null | undefined): ModuleDefinition[] {
  return MODULES
    .filter((module) => canAccessModule(user, module.key as ModuleKey))
    .sort((left, right) => left.order - right.order);
}

export function resolveVisibleModules(user: AuthenticatedUser | null | undefined): ModuleDefinition[] {
  return resolveAccessibleModules(user).filter((module) => module.showOnDesktop !== false);
}

export function resolveNavigationContext(user: AuthenticatedUser | null | undefined): ResolvedNavigationContext {
  const access = resolveEffectiveAccess(user);
  const properties = access.memberships.map((membership) => ({
    id: membership.tenantId,
    label: getPropertyLabel(membership.tenantId),
    isDefault: Boolean(membership.isDefault) || membership.tenantId === access.tenantId,
  }));
  const accessibleModules = resolveAccessibleModules(user);
  const visibleModules = resolveVisibleModules(user);
  const defaultLandingPath = accessibleModules[0] ? getModulePath(accessibleModules[0]) : null;

  if (!access.hasAssignedRole) {
    return {
      access,
      properties,
      accessibleModules,
      visibleModules,
      defaultLandingPath,
      state: "no_role",
    };
  }

  if (properties.length === 0) {
    return {
      access,
      properties,
      accessibleModules,
      visibleModules,
      defaultLandingPath,
      state: "no_property",
    };
  }

  if (accessibleModules.length === 0) {
    return {
      access,
      properties,
      accessibleModules,
      visibleModules,
      defaultLandingPath,
      state: "no_modules",
    };
  }

  return {
    access,
    properties,
    accessibleModules,
    visibleModules,
    defaultLandingPath,
    state: "ready",
  };
}
